import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { Card, Badge, Button } from '@shared/components/ui';
import {
  FileText,
  Download,
  ArrowLeft,
  FileCheck,
  ShieldCheck,
  User,
  Mail,
  Briefcase,
} from 'lucide-react';

interface DPA {
  id: string;
  signer_name: string;
  signer_title: string;
  status: string;
  valid_from: string;
  valid_until: string;
  created_at: string;
  download_url?: string;
}

interface DPAResponse {
  dpas: DPA[];
  count: number;
}

export default function DataProcessingAgreement() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('compliance');
  const [dpas, setDpas] = useState<DPA[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const hasFetched = useRef(false);

  // Form state
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      void fetchDPAs();
    }
  }, []);

  const fetchDPAs = async () => {
    try {
      setLoading(true);
      const response = await api.get<DPAResponse>('/compliance/dpa');
      setDpas(response.data?.dpas || []);
      setLoadError('');
    } catch {
      setLoadError(t('dpa.errors.load'));
      setDpas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (generating) return;
    setGenerateError('');
    setSuccessMessage('');
    setGenerating(true);

    try {
      await api.post('/compliance/dpa/generate', {
        signer_name: signerName,
        signer_title: signerTitle,
        signer_email: signerEmail || undefined,
      });

      setSuccessMessage(t('dpa.generated'));
      setSignerName('');
      setSignerTitle('');
      setSignerEmail('');
      await fetchDPAs();
    } catch {
      setGenerateError(t('dpa.errors.generate'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (downloadUrl: string) => {
    if (downloading) return;
    setDownloading(downloadUrl);
    setGenerateError('');
    setSuccessMessage('');
    try {
      const urlPath = downloadUrl.replace('/api/v1', '');
      const response = await api.get(urlPath, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dpa_${new Date().toISOString().split('T')[0]}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      setGenerateError(t('dpa.errors.download'));
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, { dateStyle: 'medium' }).format(
      new Date(value)
    );
  const getStatusLabel = (status: string) => {
    const key = status.toLowerCase();
    return ['generated', 'signed', 'pending'].includes(key)
      ? t(`dpa.status.${key}`)
      : t('dpa.status.unknown', { status });
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('dpa.back')}
              disabled={generating}
              onClick={() => navigate('/profile')}
              className="p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
            >
              <ArrowLeft size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-3xl">
                {t('dpa.title')}
              </h1>
              <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark sm:text-base">
                {t('dpa.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="p-6 bg-accent-soft-light dark:bg-accent-soft-dark border border-accent-text-light/20 dark:border-accent-text-dark/20 rounded-2xl flex items-start gap-4">
          <ShieldCheck className="text-brand-600 dark:text-brand-400 shrink-0 mt-1" size={24} />
          <div>
            <h3 className="mb-2 font-semibold text-accent-text-light dark:text-accent-text-dark">
              {t('dpa.noticeTitle')}
            </h3>
            <p className="mb-3 text-sm leading-relaxed text-accent-text-light dark:text-accent-text-dark">
              {t('dpa.noticeDescription')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-medium text-brand-600 dark:text-brand-400">
              <span className="flex items-center gap-2">
                <CheckMark /> {t('dpa.topics.processing')}
              </span>
              <span className="flex items-center gap-2">
                <CheckMark /> {t('dpa.topics.security')}
              </span>
              <span className="flex items-center gap-2">
                <CheckMark /> {t('dpa.topics.breach')}
              </span>
              <span className="flex items-center gap-2">
                <CheckMark /> {t('dpa.topics.rights')}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Generator Form */}
          <Card className="min-w-0 lg:col-span-1 shadow-elevated h-fit">
            <div className="p-6 border-b border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                <FileCheck size={20} className="text-brand-600 dark:text-brand-400" />{' '}
                {t('dpa.generatorTitle')}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {(successMessage || generateError) && (
                <div
                  role={successMessage ? 'status' : 'alert'}
                  aria-live="polite"
                  className={`p-4 rounded-xl text-sm font-bold border ${
                    successMessage
                      ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                      : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
                  }`}
                >
                  {successMessage || generateError}
                </div>
              )}

              <form onSubmit={handleGenerate} className="space-y-4">
                <fieldset disabled={generating} className="space-y-4">
                  <div>
                    <label htmlFor="dpa-signer-name" className="app-field-label">
                      {t('dpa.signerName')}
                    </label>
                    <div className="relative">
                      <User
                        size={16}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark"
                      />
                      <input
                        id="dpa-signer-name"
                        type="text"
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        placeholder={t('dpa.signerNamePlaceholder')}
                        autoComplete="name"
                        required
                        className="app-field-control w-full pl-10 pr-4 focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="dpa-signer-title" className="app-field-label">
                      {t('dpa.signerTitle')}
                    </label>
                    <div className="relative">
                      <Briefcase
                        size={16}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark"
                      />
                      <input
                        id="dpa-signer-title"
                        type="text"
                        value={signerTitle}
                        onChange={(e) => setSignerTitle(e.target.value)}
                        placeholder={t('dpa.signerTitlePlaceholder')}
                        required
                        className="app-field-control w-full pl-10 pr-4 focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="dpa-signer-email" className="app-field-label">
                      {t('dpa.signerEmail')}
                    </label>
                    <div className="relative">
                      <Mail
                        size={16}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark"
                      />
                      <input
                        id="dpa-signer-email"
                        type="email"
                        value={signerEmail}
                        onChange={(e) => setSignerEmail(e.target.value)}
                        placeholder={t('dpa.signerEmailPlaceholder')}
                        autoComplete="email"
                        className="app-field-control w-full pl-10 pr-4 focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400"
                      />
                    </div>
                  </div>

                  <Button type="submit" isLoading={generating} className="w-full mt-4">
                    {generating ? t('dpa.generating') : t('dpa.generate')}
                  </Button>
                </fieldset>
              </form>
            </div>
          </Card>

          {/* DPA List */}
          <div className="min-w-0 lg:col-span-2 space-y-6">
            <h3 className="px-1 text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
              {t('dpa.agreementsTitle')}
            </h3>

            {loading ? (
              <div className="py-12 flex justify-center">
                <div
                  role="status"
                  aria-label={t('dpa.loading')}
                  className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"
                ></div>
              </div>
            ) : loadError ? (
              <Card className="space-y-4 p-6 text-center">
                <p
                  role="alert"
                  className="text-sm text-state-danger-light dark:text-state-danger-dark"
                >
                  {loadError}
                </p>
                <Button type="button" variant="secondary" onClick={() => void fetchDPAs()}>
                  {t('dpa.retry')}
                </Button>
              </Card>
            ) : dpas.length === 0 ? (
              <Card className="py-16 text-center border-2 border-dashed">
                <FileText
                  size={48}
                  className="mx-auto text-text-subtle-light dark:text-text-subtle-dark mb-4"
                />
                <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {t('dpa.emptyTitle')}
                </h4>
                <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('dpa.emptyDescription')}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {dpas.map((dpa) => (
                  <Card
                    key={dpa.id}
                    className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 group hover:border-brand-200 transition-all"
                  >
                    <div className="flex min-w-0 items-start gap-4">
                      <div className="w-12 h-12 shrink-0 rounded-2xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center text-brand-600 dark:text-brand-400 font-black text-lg">
                        DPA
                      </div>
                      <div className="min-w-0 break-words">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h4 className="min-w-0 break-all font-bold text-text-primary-light dark:text-text-primary-dark">
                            {dpa.signer_name}
                          </h4>
                          <Badge
                            variant={dpa.status.toLowerCase() === 'signed' ? 'success' : 'info'}
                          >
                            {getStatusLabel(dpa.status)}
                          </Badge>
                        </div>
                        <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium">
                          {dpa.signer_title}
                        </p>
                        <div className="mt-2 text-xs text-text-subtle-light dark:text-text-subtle-dark font-mono">
                          {t('dpa.validity', {
                            from: formatDate(dpa.valid_from),
                            until: formatDate(dpa.valid_until),
                          })}
                        </div>
                      </div>
                    </div>

                    {dpa.download_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(dpa.download_url!)}
                        disabled={downloading !== null}
                        className="shrink-0"
                      >
                        <Download size={16} className="mr-2" /> {t('dpa.download')}
                      </Button>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function CheckMark() {
  return (
    <div className="w-4 h-4 rounded-full bg-accent-soft-light dark:bg-accent-soft-dark flex items-center justify-center shrink-0">
      <svg
        className="w-2.5 h-2.5 text-accent-text-light dark:text-accent-text-dark"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

// Add icon support to Input component (quick patch for this file)
// In a real scenario, I'd update the shared Input component
declare module '@shared/components/ui' {
  interface InputProps {
    icon?: React.ReactNode;
  }
}
